// pages/Management.js
import { useEffect, useRef } from "react";
import TopBar from "../components/TopBar";
import Header from "../components/Header";
import PreFooter from "../components/PreFooter";
import Footer from "../components/Footer";

const committeeMembers = [
  {
    id: "feature",
    type: "feature",
    icon: "🎓",
    title: "Deputy Vice-Chancellor",
    description:
      "The Deputy Vice-Chancellor shall serve as the ex-officio Director of the Centre, providing overarching leadership and institutional direction to the Media & Community Relations Centre.",
  },
  {
    num: 2,
    role: "Deputy Director — Media",
    tag: "Academic Staff",
    tagClass: "amber",
    accentClass: "gold",
    desc: "Academic staff member with expertise in the field of media, appointed by the Vice-Chancellor.",
  },
  {
    num: 2,
    role: "Deputy Director — Community Relations",
    tag: "Academic Staff",
    tagClass: "amber",
    accentClass: "gold",
    desc: "Academic staff member with expertise in community relations, appointed by the Vice-Chancellor.",
  },
  {
    num: 3,
    role: "Academic Staff Member",
    tag: "VC Appointed",
    tagClass: "",
    accentClass: "",
    desc: "Academic staff member with knowledge in media and community relations, appointed by the Vice-Chancellor.",
  },
  {
    num: 4,
    role: "Director / NCSU",
    tag: "Ex-Officio",
    tagClass: "blue",
    accentClass: "navy",
    desc: "Director of the National Cadet Service Unit.",
  },
  {
    num: 5,
    role: "Director / ITC",
    tag: "Ex-Officio",
    tagClass: "blue",
    accentClass: "navy",
    desc: "Director of the Information Technology Centre.",
  },
  {
    num: 6,
    role: "Director / Student Welfare",
    tag: "Ex-Officio",
    tagClass: "blue",
    accentClass: "navy",
    desc: "Director of the Student Welfare Division.",
  },
  {
    num: 7,
    role: "Director / INRO, BLITTO, URC",
    tag: "Ex-Officio",
    tagClass: "blue",
    accentClass: "navy",
    desc: "Director overseeing INRO, BLITTO, and the University Research Committee.",
  },
  {
    num: 8,
    role: "Proctor",
    tag: "University Officer",
    tagClass: "",
    accentClass: "",
    desc: "University Proctor responsible for student discipline and conduct.",
  },
  {
    num: 9,
    role: "Academic Representatives — Each Faculty & PG Institute",
    tag: "Faculty Nominees",
    tagClass: "green",
    accentClass: "teal",
    fullWidth: true,
    desc: "One academic representative from each faculty and postgraduate institute of the University of Peradeniya, nominated to represent their respective academic unit.",
    faculties: [
      "Faculty of Agriculture",
      "Faculty of Allied Health Sciences",
      "Faculty of Arts",
      "Faculty of Dental Sciences",
      "Faculty of Engineering",
      "Faculty of Management",
      "Faculty of Medicine",
      "Faculty of Science",
      "Faculty of Veterinary Medicine",
      "Postgraduate Institute of Agriculture",
      "Postgraduate Institute of Science",
    ],
  },
  {
    num: 10,
    role: "Senior Administrative Officer",
    tag: "Registrar's Nominee",
    tagClass: "",
    accentClass: "",
    desc: "A Senior Administrative Officer nominated by the Registrar of the University.",
  },
  {
    num: 11,
    role: "Senior Administrative Officers",
    tag: "Administrative",
    tagClass: "",
    accentClass: "",
    desc: "Senior Administrative Officers of Student Registration and Student Services.",
  },
  {
    num: 12,
    role: "Statistical Officer",
    tag: "University Officer",
    tagClass: "blue",
    accentClass: "navy",
    desc: "The Statistical Officer of the University of Peradeniya.",
  },
  {
    num: 13,
    role: "Alumni / Media Industry Experts",
    tag: "By Invitation · 2 Members",
    tagClass: "amber",
    accentClass: "gold",
    desc: "Two alumni or experts in the media industry, invited to contribute professional and industry perspectives.",
  },
  {
    num: 14,
    role: "Public Relations Officer",
    tag: "Secretary of the Committee",
    tagClass: "secretary",
    accentClass: "secretary",
    desc: "The Public Relations Officer serves as the Secretary of the Management Committee.",
  },
  {
    num: 15,
    role: "Webmaster",
    tag: "Technical Officer",
    tagClass: "green",
    accentClass: "teal",
    desc: "The University Webmaster responsible for digital and web presence.",
  },
];

function Management() {
  const introRef        = useRef(null);
  const sectionLabelRef = useRef(null);
  const cardRefs        = useRef([]);

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;

          if (el.classList.contains("js-animate")) {
            el.classList.add("animate-in");
          }
          if (el.classList.contains("js-card")) {
            const siblings = [...el.parentElement.querySelectorAll(".js-card")];
            const idx = siblings.indexOf(el);
            setTimeout(() => el.classList.add("animate-in"), idx * 100);
          }
          io.unobserve(el);
        });
      },
      { threshold: 0.12 }
    );

    if (introRef.current)        io.observe(introRef.current);
    if (sectionLabelRef.current) io.observe(sectionLabelRef.current);
    cardRefs.current.forEach((card) => { if (card) io.observe(card); });

    return () => io.disconnect();
  }, []);

  return (
    <>
      <TopBar />
      <Header activePage="about" />

      {/* ── Page Hero with background image ── */}
      <section className="page-hero" style={heroStyle}>

        {/* Background image layer */}
        <div style={heroBgStyle}></div>

        {/* Dark gradient overlay */}
        <div style={heroOverlayStyle}></div>

        {/* Decorative diamond pattern */}
        <div className="hero-pattern">
          {Array.from({ length: 32 }).map((_, i) => <span key={i}></span>)}
        </div>

        <div className="hero-text-block" style={{ position: "relative", zIndex: 3 }}>
          <div className="breadcrumb">
            <a href="/">Home</a>
            <span className="breadcrumb-sep">›</span>
            <span>About</span>
            <span className="breadcrumb-sep">›</span>
            <span>Management Committee</span>
          </div>
          <h2>Management <span>Committee</span></h2>
          <div className="hero-rule"></div>
          <p>Governing the Media &amp; Community Relations Centre with excellence and vision</p>
        </div>
      </section>

      {/* Intro Strip */}
      <div className="intro-strip">
        <div className="intro-inner js-animate" ref={introRef}>
          <p>
            The <strong>Management Committee</strong> of the Media &amp; Community Relations Centre
            oversees the strategic direction, operations, and community engagement initiatives of
            the Centre at the <strong>University of Peradeniya</strong>. The committee comprises
            distinguished academic, administrative, and industry representatives.
          </p>
        </div>
      </div>

      {/* Main Content */}
      <main className="main-content">
        <div className="section-label js-animate" ref={sectionLabelRef}>
          <div className="label-line"></div>
          <span className="label-text">Committee Composition</span>
          <div className="label-line-right"></div>
        </div>

        <div className="committee-grid">
          {committeeMembers.map((member, index) => {
            if (member.type === "feature") {
              return (
                <div
                  key={index}
                  className="feature-card js-card"
                  ref={(el) => (cardRefs.current[index] = el)}
                >
                  <div className="feature-icon">{member.icon}</div>
                  <div className="feature-text">
                    <h3>{member.title}</h3>
                    <p dangerouslySetInnerHTML={{ __html: member.description }}></p>
                  </div>
                </div>
              );
            }

            return (
              <div
                key={index}
                className={`member-card js-card${member.fullWidth ? " full-width" : ""}`}
                ref={(el) => (cardRefs.current[index] = el)}
                style={member.fullWidth ? { gridColumn: "1 / -1" } : {}}
              >
                <div
                  className={`card-accent${member.accentClass ? " " + member.accentClass : ""}`}
                  style={
                    member.accentClass === "secretary"
                      ? { background: "linear-gradient(180deg, #800000, #c9a676)" }
                      : {}
                  }
                ></div>
                <div className="card-inner">
                  <div className="member-number">{member.num}</div>
                  <div className="member-info">
                    <div className="member-role">{member.role}</div>
                    <span
                      className={`member-tag${member.tagClass ? " " + member.tagClass : ""}`}
                      style={
                        member.accentClass === "secretary"
                          ? { background: "#f9f0e0", color: "#7a4e00" }
                          : {}
                      }
                    >
                      {member.tag}
                    </span>
                    <p style={{ fontSize: "13px", color: "#666", marginTop: "6px" }}>
                      {member.desc}
                    </p>

                    {member.faculties && (
                      <div className="roles-callout" style={{ marginTop: "14px" }}>
                        <div className="roles-list">
                          {member.faculties.map((faculty, fi) => (
                            <span className="role-pill" key={fi}>{faculty}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </main>

      <PreFooter />
      <Footer />
    </>
  );
}

export default Management;

/* ─── Hero styles ─── */

/* Keep the existing .page-hero sizing/flex from CSS,
   just layer the image behind via inline styles */
const heroStyle = {
  position: "relative",
};

/* The actual photo — sits behind everything */
const heroBgStyle = {
  position: "absolute",
  inset: 0,
  backgroundImage: "url('/management.jpg')",
  backgroundSize: "cover",
  backgroundPosition: "center center",
  zIndex: 0,
};

/* Deep maroon gradient over the photo — matches the screenshot's rich dark red look */
const heroOverlayStyle = {
  position: "absolute",
  inset: 0,
  background: [
    "linear-gradient(135deg,",
    "  rgba(60, 0, 0, 0.82) 0%,",
    "  rgba(128, 0, 0, 0.78) 50%,",
    "  rgba(90, 0, 0, 0.88) 100%",
    ")",
  ].join(""),
  zIndex: 1,
};